import { leadName } from './types';
import type { Lead } from './types';

/**
 * Sorting the leads table by a column.
 *
 * ── Sort, not filter ───────────────────────────────────────────────────────
 *
 * Kyu asked for this as "filter per column title", meaning HubSpot's column
 * headers. They are two different controls and the distinction is worth
 * holding on to: a filter REMOVES rows and shows a count that changed; a sort
 * REORDERS the same rows and the count never moves. Everything here is the
 * second one, and it composes with the filters rather than replacing them.
 *
 * ── The rule that decides whether this is useful or useless ────────────────
 *
 * BLANKS ALWAYS SORT LAST, in both directions.
 *
 * Not "blanks are the earliest date" and not "blanks are the latest date".
 * 600-odd leads have no next action and 100-odd have never been touched, so a
 * blank treated as an extreme puts hundreds of empty rows at the top on the
 * first click and the column is dead on arrival. A missing value is not a low
 * value or a high one — it is an absence, and an absence belongs at the end of
 * whatever order you asked for.
 *
 * Kyu approved this rule explicitly before it was built, because it is the one
 * decision here that a reader cannot see and would have to discover.
 *
 * ── Ties ───────────────────────────────────────────────────────────────────
 *
 * Broken by name, then by id. Without a final tiebreak, two leads with the
 * same last-touch day swap places every time React re-renders the list, which
 * reads as the table flickering while you try to click a row.
 */

export type SortColumn = 'name' | 'email' | 'company' | 'lastTouch' | 'nextAction';

export type SortDirection = 'asc' | 'desc';

export interface LeadSort {
  column: SortColumn;
  direction: SortDirection;
}

/**
 * What each column says it will do, in the words the header uses.
 *
 * Held here rather than in the component so the header's tooltip and the
 * comparator can never describe different orders — the classic way a sort
 * control starts lying about itself.
 */
export const SORT_LABELS: Record<SortColumn, { asc: string; desc: string }> = {
  name: { asc: 'A to Z', desc: 'Z to A' },
  email: { asc: 'A to Z', desc: 'Z to A' },
  company: { asc: 'A to Z', desc: 'Z to A' },
  lastTouch: { asc: 'Oldest first', desc: 'Most recent first' },
  nextAction: { asc: 'Soonest first', desc: 'Furthest away first' },
};

/**
 * Which way a column starts when you first click it.
 *
 * Text starts A→Z because that is what a person means by "sort by name". Dates
 * start at the end people actually want: the most recent touch, and the
 * soonest thing due. Starting every column ascending would make Last touch
 * open on the leads nobody has spoken to since June.
 */
const FIRST_DIRECTION: Record<SortColumn, SortDirection> = {
  name: 'asc',
  email: 'asc',
  company: 'asc',
  lastTouch: 'desc',
  nextAction: 'asc',
};

/**
 * Clicking a header: off → first direction → the other one → off.
 *
 * Three states, not two. A sort you cannot turn off leaves no way back to the
 * list's own order — newest lead first — which is what somebody wants after
 * they have finished looking at one particular ordering.
 */
export function nextSort(current: LeadSort | null, column: SortColumn): LeadSort | null {
  if (current === null || current.column !== column) {
    return { column, direction: FIRST_DIRECTION[column] };
  }
  if (current.direction === FIRST_DIRECTION[column]) {
    return { column, direction: current.direction === 'asc' ? 'desc' : 'asc' };
  }
  return null;
}

export interface SortContext {
  /** company_id → the company record's name. */
  companyNames: ReadonlyMap<string, string>;
  /** lead id → the soonest open follow-up's due date, if any. */
  nextActionDue: ReadonlyMap<string, string | null>;
}

/**
 * The value a column sorts on, or null when the lead has nothing there.
 *
 * Text is lowercased so "alison" and "Alison" do not end up in two different
 * places — the reader sees one alphabet, not ASCII's two.
 */
function valueOf(lead: Lead, column: SortColumn, context: SortContext): string | null {
  switch (column) {
    case 'name': {
      // leadName falls back to the email and then to "(no name)", so it is
      // never blank. Sorting on the raw first/last name would file every
      // nameless lead under the empty string and hide them at the end, when
      // they DO have something to sort by.
      return leadName(lead).toLowerCase();
    }
    case 'email': {
      const email = lead.email.trim();
      return email === '' ? null : email.toLowerCase();
    }
    case 'company': {
      // The linked record's name first, the typed string second. Those two
      // disagree on real data — one company record carries leads naming
      // nineteen different companies — and the record is the one the Company
      // column prints.
      const linked =
        lead.company_id === null ? undefined : context.companyNames.get(lead.company_id);
      const name = (linked ?? lead.company_name).trim();
      return name === '' ? null : name.toLowerCase();
    }
    case 'lastTouch': {
      return lead.last_touch_at;
    }
    case 'nextAction': {
      return context.nextActionDue.get(lead.id) ?? null;
    }
  }
}

/**
 * A stable, blanks-last ordering. Returns a new array; the input is untouched.
 */
export function sortLeads(
  leads: readonly Lead[],
  sort: LeadSort | null,
  context: SortContext,
): Lead[] {
  if (sort === null) {
    return [...leads];
  }
  const flip = sort.direction === 'desc' ? -1 : 1;
  return [...leads].sort((a, b) => {
    const left = valueOf(a, sort.column, context);
    const right = valueOf(b, sort.column, context);
    // Blanks last in BOTH directions, so this is decided before `flip` is
    // applied and never multiplied by it.
    if (left === null && right === null) {
      return tiebreak(a, b);
    }
    if (left === null) {
      return 1;
    }
    if (right === null) {
      return -1;
    }
    if (left === right) {
      return tiebreak(a, b);
    }
    return (left < right ? -1 : 1) * flip;
  });
}

/**
 * Name, then id. The id is what makes the order TOTAL: two leads can share a
 * name, and without a final unique key the browser's sort is free to return
 * them in either order on each render.
 */
function tiebreak(a: Lead, b: Lead): number {
  const nameA = leadName(a).toLowerCase();
  const nameB = leadName(b).toLowerCase();
  if (nameA !== nameB) {
    return nameA < nameB ? -1 : 1;
  }
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}
